// components/GoogleReviews.jsx
// Presentational only. Receives `data` from getGoogleReviews().
import { useCallback, useEffect, useRef, useState } from "react";

function Stars({ rating }) {
  const full = Math.round(rating || 0);
  return (
    <span
      aria-label={`${rating} out of 5 stars`}
      style={{ color: "#fbbc04", letterSpacing: 2 }}
    >
      {"★".repeat(full)}
      <span style={{ color: "#ddd" }}>{"★".repeat(5 - full)}</span>
    </span>
  );
}

function ReviewCard({
  r,
  index,
  total,
  readMoreLabel = "Read more",
  showLessLabel = "Show less",
}) {
  const textRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);

  // Only measure while collapsed, so the button stays after expanding
  useEffect(() => {
    const el = textRef.current;
    if (!el) return;

    const check = () => {
      if (!expanded) setOverflowing(el.scrollHeight > el.clientHeight + 1);
    };

    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [expanded, r.text]);

  return (
    <article
      aria-roledescription="slide"
      aria-label={`${index + 1} of ${total}`}
      className="box-border shrink-0 basis-[88%] snap-start rounded-xl border border-[#e5e5e5] bg-white p-5 sm:basis-[calc(50%-10px)] lg:basis-[calc(33.333%-14px)]"
    >
      <header
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 12,
        }}
      >
        {r.photo && (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={r.photo}
            alt=""
            width={40}
            height={40}
            loading="lazy"
            referrerPolicy="no-referrer"
            style={{ borderRadius: "50%" }}
          />
        )}
        <div>
          {r.authorUrl ? (
            <a
              href={r.authorUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontWeight: 600 }}
            >
              {r.author}
            </a>
          ) : (
            <strong>{r.author}</strong>
          )}
          <div style={{ fontSize: 12, color: "#777" }}>{r.when}</div>
        </div>
      </header>

      <Stars rating={r.rating} />

      <p
        ref={textRef}
        style={{
          marginTop: 8,
          marginBottom: 0,
          lineHeight: 1.6,
          fontSize: 15,
          ...(expanded
            ? {}
            : {
                display: "-webkit-box",
                WebkitLineClamp: 4,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }),
        }}
      >
        {r.text}
      </p>

      {(overflowing || expanded) && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          style={{
            marginTop: 8,
            padding: 0,
            background: "none",
            border: 0,
            color: "#f64a62",
            fontSize: 14,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          {expanded ? showLessLabel : readMoreLabel}
        </button>
      )}
    </article>
  );
}

const arrowBtn =
  "flex h-11 w-11 items-center justify-center rounded-full border border-[#e5e5e5] bg-white text-[22px] leading-none text-[#222] shadow-sm transition hover:bg-[#f5f5f5] disabled:cursor-not-allowed disabled:opacity-40";

export default function GoogleReviews({
  data,
  limit = 5,
  readMoreLabel,
  showLessLabel,
}) {
  const reviews = (data?.reviews || [])
    .filter((r) => r.rating === 5 && r.text)
    .slice(0, limit);

  const trackRef = useRef(null);
  const [isRtl, setIsRtl] = useState(false);
  const [page, setPage] = useState(0);
  const [pageCount, setPageCount] = useState(1);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const update = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;

    // scrollLeft is negative in RTL, so work with the absolute value
    const pos = Math.abs(el.scrollLeft);
    const max = el.scrollWidth - el.clientWidth;

    setIsRtl(getComputedStyle(el).direction === "rtl");
    setPageCount(Math.max(1, Math.round(el.scrollWidth / el.clientWidth)));
    setPage(Math.round(pos / el.clientWidth));
    setCanPrev(pos > 4);
    setCanNext(pos < max - 4);
  }, []);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;

    update();
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [update, reviews.length]);

  if (!data || reviews.length === 0) return null;

  const goToPage = (index) => {
    const el = trackRef.current;
    if (!el) return;
    const dir = isRtl ? -1 : 1;
    el.scrollTo({ left: dir * index * el.clientWidth, behavior: "smooth" });
  };

  const step = (delta) => {
    const el = trackRef.current;
    if (!el) return;
    const dir = isRtl ? -1 : 1;
    el.scrollBy({ left: dir * delta * el.clientWidth, behavior: "smooth" });
  };

  return (
    <section
      aria-roledescription="carousel"
      aria-label="Google reviews"
      className="w-full box-border text-text-primary px-[24px] md:px-[60px] lg:px-[80px] xl:px-[80px] pb-[40px] md:pb-[40px] lg:pb-[60px] xl:pb-[60px] max-w-[1600px] mx-auto"
    >
      <div style={{ textAlign: "center", marginBottom: 32 }}>
        <h2 style={{ fontSize: 32, marginBottom: 8 }}>What Our Clients Say</h2>
        <div style={{ fontSize: 18 }}>
          <strong>{data.rating?.toFixed(1)}</strong>{" "}
          <Stars rating={data.rating} />{" "}
          {data.mapsUrl && (
            <a href={data.mapsUrl} target="_blank" rel="noopener noreferrer">
              {data.total} Google reviews
            </a>
          )}
        </div>
      </div>

      {/* Track: items-start so expanding one card doesn't stretch the others */}
      <div
        ref={trackRef}
        className="flex snap-x snap-mandatory items-start gap-5 overflow-x-auto scroll-smooth pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {reviews.map((r, i) => (
          <ReviewCard
            key={i}
            r={r}
            index={i}
            total={reviews.length}
            readMoreLabel={readMoreLabel}
            showLessLabel={showLessLabel}
          />
        ))}
      </div>

      {/* Controls: first button is "previous", so it lands on the right in RTL */}
      {pageCount > 1 && (
        <div className="mt-6 flex items-center justify-center gap-4">
          <button
            type="button"
            className={arrowBtn}
            onClick={() => step(-1)}
            disabled={!canPrev}
            aria-label="Previous reviews"
          >
            {isRtl ? "›" : "‹"}
          </button>

          <div className="flex items-center gap-2">
            {Array.from({ length: pageCount }, (_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => goToPage(i)}
                aria-label={`Go to page ${i + 1}`}
                aria-current={i === page}
                className={`h-2.5 rounded-full transition-all ${
                  i === page ? "w-6 bg-[#f64a62]" : "w-2.5 bg-[#d4d4d4]"
                }`}
              />
            ))}
          </div>

          <button
            type="button"
            className={arrowBtn}
            onClick={() => step(1)}
            disabled={!canNext}
            aria-label="Next reviews"
          >
            {isRtl ? "‹" : "›"}
          </button>
        </div>
      )}

      <p
        style={{
          textAlign: "center",
          marginTop: 24,
          fontSize: 13,
          color: "#777",
        }}
      >
        Reviews from Google
      </p>
    </section>
  );
}
